import { Box, HStack, IconButton, Tag, Text } from '@chakra-ui/react'
import { IconPencil as Edit, IconStar as Star, IconChevronRight } from '@tabler/icons-react'
import React from 'react'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { Agent, MAIN_CHAT_AGENT_SLUG } from '../../../types/agentTypes'
import { getIconByName } from '../../../utils/iconUtils'
import { markdownToPlainText } from '../../../core/parsers/markdownParser'

interface AgentCardProps {
  agent: Agent
  isSelected?: boolean
  // Passed down from the page: calling useUser() per card would refetch the
  // (staleTime: 0) user query on every card mount.
  isSuperuser?: boolean
  onSelect: (agent: Agent) => void
  onEdit?: (agent: Agent) => void
  onToggleFavorite?: (agentId: string) => void
}

const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  isSelected = false,
  isSuperuser = false,
  onSelect,
  onEdit,
  onToggleFavorite,
}) => {
  const { t } = useLanguage()
  const isMainChat = agent.slug === MAIN_CHAT_AGENT_SLUG

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onToggleFavorite) {
      onToggleFavorite(agent.id)
    }
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onEdit) {
      onEdit(agent)
    }
  }

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(agent)
  }

  // Only superusers may edit; the library decides which agents it shows.
  const showEditButton = !!onEdit && isSuperuser

  return (
    <Box
      id={`agent-card-${agent.id}`}
      position='relative'
      borderRadius='lg'
      bg='transparent'
      transition='background 0.15s'
      sx={{
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': { bg: 'chat.selected', '& .agent-card-text': { color: 'chat.brand' } },
        },
      }}
    >
      <Box
        as='button'
        type='button'
        w='100%'
        textAlign='left'
        display='flex'
        gap={4}
        alignItems='flex-start'
        p={4}
        pr={10}
        pb={showEditButton || onToggleFavorite ? 10 : 4}
        minH='126px'
        borderRadius='lg'
        onClick={handleSelectClick}
        aria-label={`${t('library.select')}: ${agent.name}${isMainChat ? ` (${t('library.mainChat')})` : ''}`}
        _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus' }}
      >
        <Box
          boxSize={12}
          flexShrink={0}
          borderRadius='lg'
          bg={isSelected ? 'chat.iconSurface' : 'chat.bubble'}
          color={isSelected ? 'chat.iconInk' : 'chat.ink'}
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          {getIconByName(agent.profile_picture_url, 26) || getIconByName('Bot', 26)}
        </Box>
        <Box minW={0} flex='1' pt={0.5}>
          <HStack spacing={2} mb={1} minW={0}>
            <Text
              className='agent-card-text'
              fontSize='md'
              fontWeight='bold'
              lineHeight='1.4'
              color='chat.ink'
              noOfLines={1}
            >
              {agent.name}
            </Text>
            {isMainChat && (
              <Tag
                size='sm'
                borderRadius='full'
                bg='chat.userBubble'
                color='chat.userInk'
                border='1px solid'
                borderColor='chat.focus'
                flexShrink={0}
              >
                {t('library.mainChat')}
              </Tag>
            )}
          </HStack>
          <Text className='agent-card-text' fontSize='sm' color='chat.muted' lineHeight='1.55' noOfLines={2}>
            {agent.description ? markdownToPlainText(agent.description) : t('library.noDescription')}
          </Text>
        </Box>
        <Box position='absolute' right={3} top={8} color='chat.muted'>
          <IconChevronRight size={18} strokeWidth={1.5} />
        </Box>
      </Box>
      <HStack position='absolute' left={20} bottom={2} spacing={1}>
        {onToggleFavorite && (
          <IconButton
            aria-label={agent.is_preferred ? t('library.removeFromFavorites') : t('library.addToFavorites')}
            icon={<Star size={16} fill={agent.is_preferred ? 'currentColor' : 'none'} />}
            size='sm'
            variant='ghost'
            color={agent.is_preferred ? 'chat.brand' : 'chat.muted'}
            onClick={handleFavoriteClick}
          />
        )}
        {showEditButton && (
          <IconButton
            aria-label={t('library.editAgent')}
            icon={<Edit size={16} />}
            size='sm'
            variant='ghost'
            color='chat.muted'
            onClick={handleEditClick}
          />
        )}
      </HStack>
    </Box>
  )
}

export default AgentCard
